import type { Object3D } from 'three';

import type { KeyPoseFkJointId } from '../types';

export interface KeyPoseFkJointConfig {
  id: KeyPoseFkJointId;
  boneNames: readonly string[];
  color: string;
  label: string;
}

export const KEY_POSE_FK_JOINT_IDS = [
  'head',
  'neck',
  'spine3',
  'spine2',
  'spine1',
  'spine0',
  'pelvis',
  'left_collar',
  'right_collar',
  'left_shoulder',
  'right_shoulder',
  'left_elbow',
  'right_elbow',
  'left_wrist',
  'right_wrist',
  'left_hip',
  'right_hip',
  'left_knee',
  'right_knee',
  'left_ankle',
  'right_ankle',
  'left_foot',
  'right_foot',
] as const satisfies readonly KeyPoseFkJointId[];

export const KEY_POSE_FK_JOINT_CONFIG_BY_ID: Record<
  KeyPoseFkJointId,
  KeyPoseFkJointConfig
> = {
  head: {
    id: 'head',
    boneNames: ['head', 'Head', 'c_head', 'mixamorigHead', 'mixamorig:Head'],
    color: '#faad14',
    label: 'Head',
  },
  neck: {
    id: 'neck',
    boneNames: ['neck', 'Neck', 'c_neck', 'mixamorigNeck', 'mixamorig:Neck'],
    color: '#d4b106',
    label: 'Neck',
  },
  spine3: {
    id: 'spine3',
    boneNames: [
      'spine3',
      'Spine3',
      'spine_03',
      'chest',
      'Chest',
      'upper_chest',
      'UpperChest',
      'c_spine3',
      'mixamorigSpine2',
      'mixamorig:Spine2',
    ],
    color: '#fa8c16',
    label: 'Spine 3',
  },
  spine2: {
    id: 'spine2',
    boneNames: [
      'spine2',
      'Spine2',
      'spine_02',
      'c_spine2',
      'mixamorigSpine1',
      'mixamorig:Spine1',
    ],
    color: '#ffa940',
    label: 'Spine 2',
  },
  spine1: {
    id: 'spine1',
    boneNames: [
      'spine1',
      'Spine1',
      'spine_01',
      'c_spine1',
      'mixamorigSpine',
      'mixamorig:Spine',
    ],
    color: '#ffc069',
    label: 'Spine 1',
  },
  spine0: {
    id: 'spine0',
    boneNames: ['c_spine0'],
    color: '#ffd591',
    label: 'Spine 0',
  },
  pelvis: {
    id: 'pelvis',
    boneNames: [
      'pelvis',
      'Pelvis',
      'hips',
      'Hips',
      'root',
      'c_hips',
      'mixamorigHips',
      'mixamorig:Hips',
    ],
    color: '#ff4d4f',
    label: 'Pelvis',
  },
  left_collar: {
    id: 'left_collar',
    boneNames: [
      'left_collar',
      'LeftClavicle',
      'l_clavicle',
      'clavicle_l',
      'collar_l',
      'mixamorigLeftShoulder',
      'mixamorig:LeftShoulder',
    ],
    color: '#9254de',
    label: 'Left clavicle',
  },
  right_collar: {
    id: 'right_collar',
    boneNames: [
      'right_collar',
      'RightClavicle',
      'r_clavicle',
      'clavicle_r',
      'collar_r',
      'mixamorigRightShoulder',
      'mixamorig:RightShoulder',
    ],
    color: '#ff7a45',
    label: 'Right clavicle',
  },
  left_shoulder: {
    id: 'left_shoulder',
    boneNames: [
      'left_shoulder',
      'LeftShoulder',
      'l_shoulder',
      'l_uparm',
      'shoulder_l',
      'c_l_shoulder',
      'mixamorigLeftArm',
      'mixamorig:LeftArm',
    ],
    color: '#597ef7',
    label: 'Left shoulder',
  },
  right_shoulder: {
    id: 'right_shoulder',
    boneNames: [
      'right_shoulder',
      'RightShoulder',
      'r_shoulder',
      'r_uparm',
      'shoulder_r',
      'c_r_shoulder',
      'mixamorigRightArm',
      'mixamorig:RightArm',
    ],
    color: '#f759ab',
    label: 'Right shoulder',
  },
  left_elbow: {
    id: 'left_elbow',
    boneNames: [
      'left_elbow',
      'LeftElbow',
      'left_forearm',
      'LeftForeArm',
      'l_elbow',
      'l_lowarm',
      'l_forearm',
      'elbow_l',
      'forearm_l',
      'c_l_forearm',
      'mixamorigLeftForeArm',
      'mixamorig:LeftForeArm',
    ],
    color: '#2f54eb',
    label: 'Left elbow',
  },
  right_elbow: {
    id: 'right_elbow',
    boneNames: [
      'right_elbow',
      'RightElbow',
      'right_forearm',
      'RightForeArm',
      'r_elbow',
      'r_lowarm',
      'r_forearm',
      'elbow_r',
      'forearm_r',
      'c_r_forearm',
      'mixamorigRightForeArm',
      'mixamorig:RightForeArm',
    ],
    color: '#eb2f96',
    label: 'Right elbow',
  },
  left_wrist: {
    id: 'left_wrist',
    boneNames: [
      'left_wrist',
      'left_hand',
      'LeftWrist',
      'LeftHand',
      'l_wrist',
      'l_hand',
      'wrist_l',
      'hand_l',
      'c_l_hand',
      'mixamorigLeftHand',
      'mixamorig:LeftHand',
    ],
    color: '#1d39c4',
    label: 'Left wrist',
  },
  right_wrist: {
    id: 'right_wrist',
    boneNames: [
      'right_wrist',
      'right_hand',
      'RightWrist',
      'RightHand',
      'r_wrist',
      'r_hand',
      'wrist_r',
      'hand_r',
      'c_r_hand',
      'mixamorigRightHand',
      'mixamorig:RightHand',
    ],
    color: '#c41d7f',
    label: 'Right wrist',
  },
  left_hip: {
    id: 'left_hip',
    boneNames: [
      'left_hip',
      'LeftHip',
      'left_thigh',
      'LeftUpLeg',
      'l_hip',
      'l_upleg',
      'l_thigh',
      'hip_l',
      'thigh_l',
      'c_l_thigh',
      'mixamorigLeftUpLeg',
      'mixamorig:LeftUpLeg',
    ],
    color: '#36cfc9',
    label: 'Left hip',
  },
  right_hip: {
    id: 'right_hip',
    boneNames: [
      'right_hip',
      'RightHip',
      'right_thigh',
      'RightUpLeg',
      'r_hip',
      'r_upleg',
      'r_thigh',
      'hip_r',
      'thigh_r',
      'c_r_thigh',
      'mixamorigRightUpLeg',
      'mixamorig:RightUpLeg',
    ],
    color: '#73d13d',
    label: 'Right hip',
  },
  left_knee: {
    id: 'left_knee',
    boneNames: [
      'left_knee',
      'LeftKnee',
      'left_shin',
      'LeftLeg',
      'l_knee',
      'l_lowleg',
      'l_shin',
      'knee_l',
      'shin_l',
      'c_l_shin',
      'mixamorigLeftLeg',
      'mixamorig:LeftLeg',
    ],
    color: '#13c2c2',
    label: 'Left knee',
  },
  right_knee: {
    id: 'right_knee',
    boneNames: [
      'right_knee',
      'RightKnee',
      'right_shin',
      'RightLeg',
      'r_knee',
      'r_lowleg',
      'r_shin',
      'knee_r',
      'shin_r',
      'c_r_shin',
      'mixamorigRightLeg',
      'mixamorig:RightLeg',
    ],
    color: '#52c41a',
    label: 'Right knee',
  },
  left_ankle: {
    id: 'left_ankle',
    boneNames: [
      'left_ankle',
      'LeftAnkle',
      'l_ankle',
      'l_foot',
      'l_talocrural',
      'ankle_l',
      'LeftFoot',
      'mixamorigLeftFoot',
      'mixamorig:LeftFoot',
    ],
    color: '#08979c',
    label: 'Left ankle',
  },
  right_ankle: {
    id: 'right_ankle',
    boneNames: [
      'right_ankle',
      'RightAnkle',
      'r_ankle',
      'r_foot',
      'r_talocrural',
      'ankle_r',
      'RightFoot',
      'mixamorigRightFoot',
      'mixamorig:RightFoot',
    ],
    color: '#389e0d',
    label: 'Right ankle',
  },
  left_foot: {
    id: 'left_foot',
    boneNames: [
      'left_foot',
      'left_toe',
      'LeftToe',
      'l_toe',
      'l_ball',
      'ltoes',
      'toe_l',
      'ball_l',
      'c_toes_fk.l',
      'leg_left_toes_01',
      'mixamorigLeftToeBase',
      'mixamorig:LeftToeBase',
    ],
    color: '#006d75',
    label: 'Left foot',
  },
  right_foot: {
    id: 'right_foot',
    boneNames: [
      'right_foot',
      'right_toe',
      'RightToe',
      'r_toe',
      'r_ball',
      'rtoes',
      'toe_r',
      'ball_r',
      'c_toes_fk.r',
      'leg_right_toes_01',
      'mixamorigRightToeBase',
      'mixamorig:RightToeBase',
    ],
    color: '#237804',
    label: 'Right foot',
  },
};

const KEY_POSE_FK_JOINT_ID_SET = new Set<KeyPoseFkJointId>(
  KEY_POSE_FK_JOINT_IDS,
);

const STRUCTURAL_FALLBACK_BY_JOINT_ID: Partial<
  Record<
    KeyPoseFkJointId,
    {
      sourceJointId: KeyPoseFkJointId;
      boneParentSteps?: number;
      boneChildSteps?: number;
    }
  >
> = {
  pelvis: { sourceJointId: 'left_ankle', boneParentSteps: 3 },
  neck: { sourceJointId: 'head', boneParentSteps: 1 },
  spine3: { sourceJointId: 'neck', boneChildSteps: 1 },
  spine2: { sourceJointId: 'spine3', boneParentSteps: 1 },
  spine1: { sourceJointId: 'spine3', boneParentSteps: 2 },
  spine0: { sourceJointId: 'spine3', boneParentSteps: 3 },
  left_elbow: { sourceJointId: 'left_wrist', boneParentSteps: 1 },
  right_elbow: { sourceJointId: 'right_wrist', boneParentSteps: 1 },
  left_collar: { sourceJointId: 'left_wrist', boneParentSteps: 3 },
  right_collar: { sourceJointId: 'right_wrist', boneParentSteps: 3 },
  left_shoulder: { sourceJointId: 'left_wrist', boneParentSteps: 2 },
  right_shoulder: { sourceJointId: 'right_wrist', boneParentSteps: 2 },
  left_knee: { sourceJointId: 'left_ankle', boneParentSteps: 1 },
  right_knee: { sourceJointId: 'right_ankle', boneParentSteps: 1 },
  left_hip: { sourceJointId: 'left_ankle', boneParentSteps: 2 },
  right_hip: { sourceJointId: 'right_ankle', boneParentSteps: 2 },
  left_foot: { sourceJointId: 'left_ankle', boneChildSteps: 1 },
  right_foot: { sourceJointId: 'right_ankle', boneChildSteps: 1 },
};

export function isKeyPoseFkJointId(value: string): value is KeyPoseFkJointId {
  return KEY_POSE_FK_JOINT_ID_SET.has(value as KeyPoseFkJointId);
}

export function resolveKeyPoseFkJointObject(
  scene: Object3D,
  jointId: KeyPoseFkJointId,
) {
  if (jointId === 'pelvis') {
    return (
      resolveStructuralKeyPoseFkJointObject(scene, jointId) ??
      resolveDirectKeyPoseFkJointObject(scene, jointId)
    );
  }

  return (
    resolveDirectKeyPoseFkJointObject(scene, jointId) ??
    resolveStructuralKeyPoseFkJointObject(scene, jointId)
  );
}

function resolveDirectKeyPoseFkJointObject(
  scene: Object3D,
  jointId: KeyPoseFkJointId,
) {
  const config = KEY_POSE_FK_JOINT_CONFIG_BY_ID[jointId];

  for (const boneName of config.boneNames) {
    const namedBone = scene.getObjectByName(boneName);
    if (isBone(namedBone)) {
      return namedBone;
    }
  }

  for (const boneName of config.boneNames) {
    const namedObject = scene.getObjectByName(boneName);
    if (namedObject) {
      return namedObject;
    }
  }

  let matchedObject: Object3D | null = null;
  scene.traverse((object) => {
    if (matchedObject || !isBone(object)) {
      return;
    }

    if (hasMatchingUserDataName(object, config.boneNames)) {
      matchedObject = object;
    }
  });

  if (matchedObject) {
    return matchedObject;
  }

  scene.traverse((object) => {
    if (matchedObject) {
      return;
    }

    if (hasMatchingUserDataName(object, config.boneNames)) {
      matchedObject = object;
    }
  });

  return matchedObject;
}

function resolveStructuralKeyPoseFkJointObject(
  scene: Object3D,
  jointId: KeyPoseFkJointId,
) {
  const fallback = STRUCTURAL_FALLBACK_BY_JOINT_ID[jointId];
  if (!fallback) {
    return null;
  }

  const sourceObject = resolveDirectKeyPoseFkJointObject(
    scene,
    fallback.sourceJointId,
  );
  if (!sourceObject) {
    return null;
  }

  let current: Object3D | null = sourceObject;

  if (fallback.boneChildSteps) {
    for (let index = 0; index < fallback.boneChildSteps; index += 1) {
      current = getFirstBoneChild(current);
      if (!current) {
        return null;
      }
    }

    return current;
  }

  for (let index = 0; index < (fallback.boneParentSteps ?? 0); index += 1) {
    current = getClosestBoneParent(current);
    if (!current) {
      return null;
    }
  }

  return current;
}

function hasMatchingUserDataName(
  object: Object3D,
  boneNames: readonly string[],
) {
  const autoAssign = getUserDataString(object, 'autoAssign');
  const originalName = getUserDataString(object, 'originalName');

  return (
    Boolean(autoAssign && boneNames.includes(autoAssign)) ||
    Boolean(originalName && boneNames.includes(originalName))
  );
}

function isBone(object: Object3D | null | undefined): object is Object3D {
  return Boolean(object && (object as Object3D & { isBone?: boolean }).isBone);
}

function getFirstBoneChild(object: Object3D): Object3D | null {
  for (const child of object.children) {
    if ((child as Object3D & { isBone?: boolean }).isBone === true) {
      return child;
    }

    const nestedChild = getFirstBoneChild(child);
    if (nestedChild) {
      return nestedChild;
    }
  }

  return null;
}

function getClosestBoneParent(object: Object3D): Object3D | null {
  let current: Object3D | null = object.parent;
  while (current) {
    if ((current as Object3D & { isBone?: boolean }).isBone === true) {
      return current;
    }

    current = current.parent;
  }

  return null;
}

function getUserDataString(object: Object3D, key: string) {
  const value = object.userData[key];

  return typeof value === 'string' ? value : null;
}
